import { Icon, IconArrowBigLeft } from "@flixbus/honeycomb-icons-react";
import { Button, Heading, Spinner, Text } from "@flixbus/honeycomb-react";
import { DefaultInfoAlert, getUserPreferredLanguage, mpoPathnames } from "@flixbus-phx/marketplace-common";
import * as React from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useFaqArticles from "../../hooks/useFaqArticles/useFaqArticles";
import { Article } from "../../types";
import * as css from "./FaqArticle.scss";

type ArticleNavigationState = Article & { searchQuery?: string; lang?: string };

const FaqArticle: React.FC = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { articleId } = useParams<{ articleId: string }>();

  const lang = getUserPreferredLanguage();

  // Try to get article from navigation state first, but only if it was loaded in the current language
  const stateArticle = location.state as ArticleNavigationState | undefined;
  const hasArticleInState = stateArticle && stateArticle.id === articleId && stateArticle.lang === lang;

  // Only fetch articles if not available in navigation state
  const { articles, loading } = useFaqArticles({ skip: hasArticleInState });

  const article = hasArticleInState ? stateArticle : articles.find((art) => art.id === articleId) || null;

  const backPath = stateArticle?.searchQuery
    ? `${mpoPathnames.faq}?q=${encodeURIComponent(stateArticle.searchQuery)}`
    : mpoPathnames.faq;

  if (loading) {
    return (
      <div className={css.spinner}>
        <Spinner />
      </div>
    );
  }

  if (!article) {
    return (
      <div>
        <DefaultInfoAlert message={formatMessage({ id: "faq.details.articleNotFound" })} />
      </div>
    );
  }

  return (
    <div>
      <div className={css.headerContainer}>
        <div className={css.innerHeaderContainer}>
          <Button appearance="link" onClick={() => navigate(backPath)} extraClasses={css.backButton}>
            <Icon InlineIcon={IconArrowBigLeft} />
            {formatMessage({ id: "faq.details.button" })}
          </Button>
          <Heading size={1} extraClasses={css.heading}>
            {article.headline}
          </Heading>
          <Text small extraClasses={css.category}>
            {article.category}
          </Text>
        </div>
      </div>

      <div className={css.content} dangerouslySetInnerHTML={{ __html: article.contentHtml }} />
    </div>
  );
};

export default FaqArticle;
